// src/services/soRuleConflicts.ts
// -------------------------------
// Detects SO-rule priority conflicts for the SO Rules admin screen.
//
// WHY THIS EXISTS. soRulesEngine runs an ORDERED, CONSUMING pass: rules are sorted
// by `priority` ascending and the first one whose selector matches an item CLAIMS
// it. Nothing — not the table, not the UI — prevents two rules sharing a priority.
// When two ACTIVE rules share one and their selectors can match the same item, the
// winner is undefined: the sort is stable, so it preserves the order the rows
// arrived in, and a Postgres read has NO guaranteed order for tied rows. The same
// request can therefore produce a different SO suffix, grouping or payee between
// runs — the hardest class of bug to chase, because it will not reproduce.
//
// A secondary sort by id (soRules.ts + soRulesEngine.ts) now makes the outcome
// STABLE, but stable is not the same as correct: it just picks one of the two
// consistently. This checker is what tells an admin the ambiguity exists so they
// can resolve it by changing a priority.
//
// Two tiers, deliberately distinguished so the warning stays meaningful:
//   * OVERLAPPING — the tied rules can match the same item. This is the hazard.
//   * NON-OVERLAPPING — tied, but their selectors are mutually exclusive (e.g. one
//     matches devices and the other fees), so neither can ever steal the other's
//     items. Untidy, not harmful. Reported as information only.
//
// Pure: no React, no DB, no I/O. Reference: docs/so-rules-engine.md.

// The selector shape this module needs. Deliberately structural rather than the
// generated row type — the admin screen, the engine and the tests all pass
// compatible objects, and only these fields participate in matching.
export interface ConflictRule {
  id?: number | string | null;
  label?: string | null;
  priority: number;
  active?: boolean | null;
  match_kind: string;
  match_fee_types: string[] | null;
  match_state: string | null;
  match_supplier_kind: string | null;
  match_supplier_suffix: string | null;
  match_payment_method: string | null;
}

export interface RuleConflict {
  priority: number;
  // The tied rules, in the order the engine would see them (id ascending).
  labels: string[];
  // true = the tied rules can claim the SAME item, so which one wins is arbitrary.
  overlapping: boolean;
}

// A null/undefined selector field is a WILDCARD — the rule doesn't care about that
// dimension, so it is compatible with any value the other rule specifies.
function scalarsCompatible(a: string | null, b: string | null): boolean {
  if (a === null || a === undefined || a === "") return true;
  if (b === null || b === undefined || b === "") return true;
  return a === b;
}

// Fee types are a LIST selector: null = any fee type. Two lists are compatible when
// they share at least one entry (either rule could claim a fee of that type).
function feeTypesCompatible(a: string[] | null, b: string[] | null): boolean {
  if (!a || a.length === 0) return true;
  if (!b || b.length === 0) return true;
  return a.some((x) => b.includes(x));
}

// Can these two rules ever claim the same item? match_kind is a required single
// value (device / fee / uniform / shoe), so two rules on different kinds can never
// collide no matter what else they say — that check comes first and is the cheapest.
export function selectorsOverlap(a: ConflictRule, b: ConflictRule): boolean {
  if (a.match_kind !== b.match_kind) return false;
  if (!feeTypesCompatible(a.match_fee_types, b.match_fee_types)) return false;
  if (!scalarsCompatible(a.match_state, b.match_state)) return false;
  if (!scalarsCompatible(a.match_supplier_kind, b.match_supplier_kind)) return false;
  if (!scalarsCompatible(a.match_supplier_suffix, b.match_supplier_suffix)) return false;
  if (!scalarsCompatible(a.match_payment_method, b.match_payment_method)) return false;
  return true;
}

function ruleName(r: ConflictRule): string {
  const l = (r.label ?? "").trim();
  return l || `${r.match_kind} rule${r.id != null ? ` #${r.id}` : ""}`;
}

// Sort key mirroring what the engine now does: priority, then id — so the labels
// are listed in the order the engine would actually evaluate them.
function byEngineOrder(a: ConflictRule, b: ConflictRule): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  return Number(a.id ?? 0) - Number(b.id ?? 0);
}

// Every priority shared by two or more ACTIVE rules, flagged by whether the tied
// rules can actually collide. INACTIVE rules are ignored entirely — the engine
// filters them out before sorting, so a deactivated rule sharing a priority is not
// a conflict and warning about it would be noise.
//
// Returned in ascending priority order. Empty array = no conflicts.
export function findPriorityConflicts(rules: ConflictRule[] | null | undefined): RuleConflict[] {
  const active = (rules ?? []).filter((r) => r.active !== false);
  const byPriority = new Map<number, ConflictRule[]>();
  for (const r of active) {
    const list = byPriority.get(r.priority);
    if (list) list.push(r);
    else byPriority.set(r.priority, [r]);
  }

  const out: RuleConflict[] = [];
  for (const [priority, group] of byPriority) {
    if (group.length < 2) continue;
    const ordered = [...group].sort(byEngineOrder);
    // Overlapping if ANY pair in the tie can claim the same item — one colliding
    // pair is enough to make the group's outcome ambiguous.
    let overlapping = false;
    for (let i = 0; i < ordered.length && !overlapping; i += 1) {
      for (let j = i + 1; j < ordered.length; j += 1) {
        if (selectorsOverlap(ordered[i], ordered[j])) {
          overlapping = true;
          break;
        }
      }
    }
    out.push({ priority, labels: ordered.map(ruleName), overlapping });
  }

  return out.sort((a, b) => a.priority - b.priority);
}

// Does saving `candidate` at this priority collide with an existing ACTIVE rule?
// Used by the edit modal to warn BEFORE the save, so a conflict is caught at the
// point it would be created. `candidate.id` is excluded so editing a rule doesn't
// report it as conflicting with itself.
export function conflictsForCandidate(
  candidate: ConflictRule,
  all: ConflictRule[] | null | undefined
): { overlapping: ConflictRule[]; sharing: ConflictRule[] } {
  if (candidate.active === false) return { overlapping: [], sharing: [] };
  const others = (all ?? []).filter(
    (r) => r.active !== false && String(r.id ?? "") !== String(candidate.id ?? "")
  );
  const sharing = others.filter((r) => r.priority === candidate.priority);
  const overlapping = sharing.filter((r) => selectorsOverlap(candidate, r));
  return { overlapping, sharing };
}
